/**
 * useSolarSimulation Hook
 *
 * Calls the solar-simulate edge function to run a PV yield model
 * (with optional perfect-foresight battery DP on top) over the site's
 * load + Octopus Agile prices + hourly GHI for the requested window.
 *
 * Returns the SimulateResult { param_hash, cached, computed_at, params,
 * monthly[], aggregate }. Results are cached server-side by param_hash;
 * pass forceRecompute to bypass the cache.
 *
 * No-export model: PV that cannot be self-consumed (and cannot charge
 * the battery, if enabled) is curtailed. This matches the physical
 * install constraint at Dalsholm.
 *
 * DP assumes perfect price foresight when a battery is supplied, so
 * the battery-enhanced saving represents a ceiling — a realisable
 * day-ahead-only dispatcher would be lower.
 */

import { useState, useEffect } from 'react'
import { supabase } from '@/supabase-client'
import { SOLAR_PRESETS } from '@/lib/solarPresets'

const DEFAULT_START = '2024-04-01'

/**
 * Default solar = first preset's params (currently Jinko Tiger Neo 625W
 * at 40 panels). Single source of truth so preset edits propagate.
 */
export const DEFAULT_SOLAR = { ...SOLAR_PRESETS[0].params }

export function useSolarSimulation({
  start = DEFAULT_START,
  end,
  solar = DEFAULT_SOLAR,
  battery = null,
  regionCode = 'N',
  meterId = null,
  fixedUnitPence,
  fixedStandingPence,
  agileStandingPence,
  lat,
  lon,
  forceRecompute = false,
  enabled = true,
} = {}) {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState(null)

  // Serialise params to stable dep strings so object identity doesn't
  // re-trigger the call on every render.
  const solarKey = JSON.stringify(solar)
  const batteryKey = JSON.stringify(battery)

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }
    let cancelled = false

    async function run() {
      setIsLoading(true)
      setError(null)

      const body = {
        start_date: start,
        end_date: end ?? new Date().toISOString().slice(0, 10),
        region_code: regionCode,
        meter_id: meterId,
        solar,
        battery,
        force_recompute: forceRecompute,
      }
      if (typeof fixedUnitPence === 'number') body.fixed_unit_pence = fixedUnitPence
      if (typeof fixedStandingPence === 'number') body.fixed_standing_pence = fixedStandingPence
      if (typeof agileStandingPence === 'number') body.agile_standing_pence = agileStandingPence
      if (typeof lat === 'number') body.lat = lat
      if (typeof lon === 'number') body.lon = lon

      const { data: result, error: fnError } = await supabase.functions.invoke(
        'solar-simulate',
        { body }
      )

      if (cancelled) return

      if (fnError) {
        console.error('[useSolarSimulation] edge function error:', fnError)
        setError(fnError.message ?? 'solar-simulate failed')
        setData(null)
      } else {
        setData(result ?? null)
      }
      setIsLoading(false)
    }

    run()
    return () => { cancelled = true }
    // solarKey + batteryKey stand in for solar/battery to avoid re-firing
    // on fresh object literals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    start, end, regionCode, meterId, solarKey, batteryKey,
    fixedUnitPence, fixedStandingPence, agileStandingPence,
    lat, lon, forceRecompute, enabled,
  ])

  return { data, isLoading, error }
}

export default useSolarSimulation
